import { useCallback, useSyncExternalStore } from "react";

/**
 * Font choice — the single source of truth for the app-wide typeface.
 *
 * The preference is `default | dyslexic` (persisted in
 * `localStorage['anyai-font']`, key removed when `default`). It is applied as
 * `html[data-font]`, which flips the `--font-stack-*` vars in tokens.css —
 * absence of the attribute is the Cendre default (Switzer). `dyslexic` swaps in
 * a dyslexia-friendly stack. The initial attribute is set by the inline boot
 * script in `index.html` before React mounts (no FOUC); this hook keeps it in
 * sync afterwards. Legacy values (`system`/`serif`/`mono`) no longer validate,
 * so an old persisted choice cleanly falls back to `default`.
 *
 * The font is also synced per-account via `/v1/me/preferences` —
 * `FontPreferenceSync` (settings feature) pushes the server value into this
 * store after auth, and the Settings control PUTs changes in the background.
 *
 * Backed by a tiny module-level store consumed via `useSyncExternalStore`,
 * mirroring `useTheme`.
 */
export type FontPreference = "default" | "dyslexic";

const STORAGE_KEY = "anyai-font";

const listeners = new Set<() => void>();

export function isFontPreference(v: unknown): v is FontPreference {
  return v === "default" || v === "dyslexic";
}

function readStoredPreference(): FontPreference {
  if (typeof localStorage === "undefined") return "default";
  const stored = localStorage.getItem(STORAGE_KEY);
  return isFontPreference(stored) ? stored : "default";
}

let preference: FontPreference = readStoredPreference();

function applyFont(font: FontPreference): void {
  if (typeof document === "undefined") return;
  if (font === "default") document.documentElement.removeAttribute("data-font");
  else document.documentElement.setAttribute("data-font", font);
}

function emit(): void {
  for (const listener of listeners) listener();
}

function setPreference(next: FontPreference): void {
  preference = next;
  if (typeof localStorage !== "undefined") {
    if (next === "default") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  }
  applyFont(next);
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getPreference(): FontPreference {
  return preference;
}

export interface UseFontResult {
  /** The user's stored choice (local mirror of the account preference). */
  font: FontPreference;
  setFont: (font: FontPreference) => void;
}

export function useFont(): UseFontResult {
  const font = useSyncExternalStore(subscribe, getPreference, getPreference);
  const setFont = useCallback((next: FontPreference) => setPreference(next), []);
  return { font, setFont };
}
